import { useContext, useEffect, useState } from 'react';
import { JobSearchResult } from '../models/job/JobSearchResult';
import { postSearchJob, getContractsForJob } from '../services/job-api';
import { isAxiosError } from 'axios';
import { CurrentPageContext } from '../reusable-components/CurrentPageContext';
import { NavigateToPageRoute } from '../utils/common-functions';
import { showErrorMessage } from '../utils/toast-functions';
import { useNavigate } from 'react-router-dom';
import { sessionExpiredMessage } from '../services/api-main';
import { JobContract } from '../models/job/JobContract';

export const errorOnFetchingJobDetailsMessage =
  'An error occurred while fetching job details.';

export const errorOnFetchingContractsMessage =
  'An error occurred while fetching contract details.';

export const useJobDetails = (token: string, jobId: string | undefined) => {
  const [job, setJob] = useState<JobSearchResult | null>(null);
  const [contracts, setContracts] = useState<JobContract[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const currentPageContext = useContext(CurrentPageContext);
  const { setCurrentRoute } = currentPageContext;
  const navigate = useNavigate();

  const fetchJobData = async (
    includeContracts = true,
    suppressErrorMessages = false,
  ) => {
    if (!jobId) {
      setError('Job ID is required');
      setLoading(false);
      return null;
    }

    try {
      // We reuse the search endpoint but with jobId as a parameter
      const result = await postSearchJob({
        token,
        jobId,
        fromDate: '',
        toDate: '',
        username: '',
        userId: '',
        companyName: '',
        companyId: '',
        state: '',
        limit: 1,
      });

      if (result.length === 0) {
        setError('Job not found');
        setJob(null);
        return null;
      }

      setJob(result[0]);
      setError(null);

      // After successfully fetching the job, fetch the contracts if requested
      if (includeContracts) {
        try {
          const contractsData = await getContractsForJob(token, jobId);
          setContracts(contractsData);
        } catch (contractError) {
          console.error('Error fetching contracts:', contractError);
          if (!suppressErrorMessages) {
            showErrorMessage(errorOnFetchingContractsMessage);
          }
          // We don't set the main error state here since we already have the job
          // Don't throw - contract errors shouldn't affect the main error state
        }
      }

      return result[0];
    } catch (error) {
      console.error('Error fetching job details:', error);
      setError(errorOnFetchingJobDetailsMessage);

      if (isAxiosError(error) && error.response?.status === 403) {
        NavigateToPageRoute(
          `/?showMessage=${sessionExpiredMessage}&messageType=warning`,
          setCurrentRoute,
          navigate,
        );
      } else {
        if (!suppressErrorMessages) {
          showErrorMessage(errorOnFetchingJobDetailsMessage);
        }
      }
      throw error; // Re-throw so caller can handle it
    }
  };

  const fetchJobDetails = async () => {
    if (!jobId) {
      setError('Job ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      await fetchJobData(true, false);
    } catch (error) {
      // Error already handled in fetchJobData
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh data after deleting a contract
  const refreshData = async () => {
    if (jobId && token) {
      setLoading(true);
      try {
        // Refresh both job details and contracts
        // Suppress error messages from fetchJobData so we can show contract error message
        await fetchJobData(true, true);
      } catch (error) {
        console.error('Error refreshing data:', error);
        showErrorMessage(errorOnFetchingContractsMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchJobDetails();
  }, [jobId, token, navigate, setCurrentRoute]);

  return { job, contracts, loading, error, refreshData };
};
